import { readContract } from '@wagmi/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { erc20Abi, formatUnits } from 'viem';
import { useConnection, useReadContract, useSignTypedData } from 'wagmi';

import { BUY_TOKEN } from '@/app/components/LlamaSwap/constants';
import { NormalizedQuote } from '@/app/components/LlamaSwap/helpers';
import { SequentialStepSection } from '@/app/components/Modal/SequentialStepModal';
import {
  buildDepositAtomicBatchTxs,
  buildUsdcSwapDepositSteps,
  computeSwapDepositProgress,
  DEPOSIT_ATOMIC_BATCH_STEP_IDS,
  SwapDepositPlanInput,
} from '@/app/components/VaultsItemPageComponents/hooks/usdcSwapDepositPlan';
import { config } from '@/app/providers/app/wagmiConfig';
import { MZUSD_DECIMALS, USDC_DECIMALS } from '@/app/shared/constants/common';
import { VaultTrancheId } from '@/app/shared/constants/vaults';
import { getTrancheAddressById } from '@/app/shared/constants/web3/chainContracts';
import { WYLDS } from '@/app/shared/constants/web3/constants';
import { useChainContracts } from '@/app/shared/hooks/blockchain/useChainContracts';
import { useCheckBatching } from '@/app/shared/hooks/blockchain/useCheckBatching';
import { useSendSequentialSteps } from '@/app/shared/hooks/blockchain/useSendSequentialSteps/useSendSequentialSteps';
import { safeParseUnits } from '@/app/shared/lib/safeParseUnits';

export const SWAP_DEPOSIT_SECTIONS: SequentialStepSection[] = [
  { id: 'swap', title: 'Swap' },
  { id: 'deposit', title: 'Deposit' },
];

type UseUsdcSwapDepositSequentialArgs = {
  usdcAmount: string;
  quote: NormalizedQuote | null;
  refetchSwapAllowance?: () => Promise<unknown>;
  trancheId: VaultTrancheId;
  vaultId?: string;
  trancheSymbol?: string;
  providerLabel?: string;
  enabled?: boolean;
  onSwapComplete?: (wyldsAmount: string) => void;
};

const APPROVE_STEP_IDS = new Set(['approve-swap', 'approve-wylds', 'approve-mzusd']);

export const useUsdcSwapDepositSequential = ({
  usdcAmount,
  quote,
  refetchSwapAllowance,
  trancheId,
  vaultId,
  trancheSymbol = 'tranche',
  providerLabel = 'aggregator',
  enabled = true,
  onSwapComplete,
}: UseUsdcSwapDepositSequentialArgs) => {
  const { address, isConnected } = useConnection();
  const { mutateAsync: signTypedData } = useSignTypedData();
  const contracts = useChainContracts(vaultId);
  const supportsBatching = useCheckBatching();

  const wyldsBeforeSwapRef = useRef<bigint | null>(null);
  const receivedWyldsRef = useRef<bigint | null>(null);
  const quoteRef = useRef(quote);
  const planInputRef = useRef<SwapDepositPlanInput | null>(null);
  const onSwapCompleteRef = useRef(onSwapComplete);
  onSwapCompleteRef.current = onSwapComplete;

  const [swapConfirmed, setSwapConfirmed] = useState(false);

  useEffect(() => {
    quoteRef.current = quote;
  }, [quote]);

  const amountUsdc = safeParseUnits(usdcAmount, USDC_DECIMALS);
  const amountMzusd = safeParseUnits(usdcAmount, MZUSD_DECIMALS);
  const trancheAddress = contracts ? getTrancheAddressById(contracts, trancheId) : undefined;

  const { data: buyTokenDecimalsOnChain } = useReadContract({
    address: WYLDS,
    abi: erc20Abi,
    functionName: 'decimals',
    query: { enabled },
  });
  const buyDecimals = buyTokenDecimalsOnChain ?? BUY_TOKEN.decimals;

  const quotedWylds = useMemo(() => {
    if (!quote?.amountReturned) return 0n;

    try {
      return BigInt(quote.amountReturned);
    } catch {
      return 0n;
    }
  }, [quote?.amountReturned]);

  const { data: mzusdBalance, refetch: refetchMzusdBalance } = useReadContract({
    address: contracts?.MZUSD,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    query: { enabled: enabled && !!contracts?.MZUSD && !!address },
  });

  const { data: wyldsBalance, refetch: refetchWyldsBalance } = useReadContract({
    address: WYLDS,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    query: { enabled: enabled && !!address },
  });

  const { data: wyldsAllowance, refetch: refetchWyldsAllowance } = useReadContract({
    address: contracts?.ASSET,
    abi: erc20Abi,
    functionName: 'allowance',
    args: address && contracts ? [address, contracts.MZUSD] : undefined,
    query: { enabled: enabled && !!address && !!contracts?.ASSET && !!contracts?.MZUSD },
  });

  const { data: mzusdAllowance, refetch: refetchMzusdAllowance } = useReadContract({
    address: contracts?.MZUSD,
    abi: erc20Abi,
    functionName: 'allowance',
    args: address && trancheAddress ? [address, trancheAddress] : undefined,
    query: { enabled: enabled && !!contracts?.MZUSD && !!trancheAddress && !!address },
  });

  const planKey = `${usdcAmount}:${trancheId}:${address ?? ''}`;

  useEffect(() => {
    setSwapConfirmed(false);
    wyldsBeforeSwapRef.current = null;
    receivedWyldsRef.current = null;
  }, [planKey]);

  const hasPlanInputs =
    enabled &&
    !!contracts &&
    !!address &&
    !!amountUsdc &&
    !!amountMzusd &&
    !!trancheAddress &&
    !!quote &&
    mzusdBalance !== undefined &&
    wyldsBalance !== undefined &&
    wyldsAllowance !== undefined &&
    mzusdAllowance !== undefined;

  const plannedSteps = useMemo(() => {
    if (
      !enabled ||
      !contracts ||
      !address ||
      !amountUsdc ||
      !amountMzusd ||
      !trancheAddress ||
      !quote ||
      mzusdBalance === undefined ||
      wyldsBalance === undefined ||
      wyldsAllowance === undefined ||
      mzusdAllowance === undefined
    ) {
      planInputRef.current = null;
      return [];
    }

    const planInput: SwapDepositPlanInput = {
      address,
      contracts,
      trancheAddress,
      trancheSymbol,
      providerLabel,
      amountUsdc,
      amountMzusd,
      quotedWylds,
      quote,
      balances: {
        mzusd: mzusdBalance,
        wylds: wyldsBalance,
        wyldsAllowance,
        mzusdAllowance,
      },
      swapConfirmed,
      session: {
        wyldsBeforeSwap: wyldsBeforeSwapRef,
        receivedWylds: receivedWyldsRef,
        quote: quoteRef,
      },
      signTypedData,
      refetchWyldsAllowance,
    };

    planInputRef.current = planInput;
    return buildUsdcSwapDepositSteps(planInput);
  }, [
    address,
    amountMzusd,
    amountUsdc,
    contracts,
    enabled,
    mzusdAllowance,
    mzusdBalance,
    providerLabel,
    quote,
    quotedWylds,
    refetchWyldsAllowance,
    signTypedData,
    swapConfirmed,
    trancheAddress,
    trancheSymbol,
    wyldsAllowance,
    wyldsBalance,
  ]);

  const refetchAllowances = () => Promise.all([refetchWyldsAllowance(), refetchMzusdAllowance()]);
  const refetchBalances = () => Promise.all([refetchMzusdBalance(), refetchWyldsBalance()]);

  const atomicBatches = useMemo(
    () =>
      supportsBatching
        ? [
            {
              stepIds: [...DEPOSIT_ATOMIC_BATCH_STEP_IDS],
              resolveCalls: (pendingStepIds: string[]) => {
                const planInput = planInputRef.current;
                if (!planInput) {
                  return Promise.reject(new Error('Deposit plan is not ready'));
                }
                return buildDepositAtomicBatchTxs(planInput, pendingStepIds);
              },
            },
          ]
        : undefined,
    [supportsBatching],
  );

  const sequential = useSendSequentialSteps({
    steps: plannedSteps,
    planKey,
    enabled: enabled && isConnected && plannedSteps.length > 0,
    stepByStep: true,
    atomicBatches,
    onStepSuccess: async (stepId) => {
      if (APPROVE_STEP_IDS.has(stepId)) {
        await Promise.all([
          refetchAllowances(),
          stepId === 'approve-swap' && refetchSwapAllowance
            ? refetchSwapAllowance()
            : Promise.resolve(),
        ]);
        return;
      }

      if (stepId === 'mint-mzusd' || stepId === 'deposit-tranche') {
        await Promise.all([refetchBalances(), refetchAllowances()]);
        return;
      }

      if (stepId !== 'swap-usdc' || !address) return;

      const after = await readContract(config, {
        address: WYLDS,
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [address],
      });

      const before = wyldsBeforeSwapRef.current ?? 0n;
      const delta = after > before ? after - before : after;

      receivedWyldsRef.current = delta > 0n ? delta : after;
      setSwapConfirmed(true);
      onSwapCompleteRef.current?.(formatUnits(receivedWyldsRef.current, buyDecimals));

      await Promise.all([refetchAllowances(), refetchBalances()]);
    },
  });

  const isReady = hasPlanInputs && isConnected && plannedSteps.length > 0;

  const swapSkipped = useMemo(() => {
    if (
      !quote ||
      mzusdBalance === undefined ||
      wyldsBalance === undefined ||
      wyldsAllowance === undefined ||
      mzusdAllowance === undefined ||
      !amountUsdc ||
      !amountMzusd
    ) {
      return false;
    }

    const progress = computeSwapDepositProgress({
      amountUsdc,
      amountMzusd,
      quotedWylds,
      quote,
      balances: {
        mzusd: mzusdBalance,
        wylds: wyldsBalance,
        wyldsAllowance,
        mzusdAllowance,
      },
      swapConfirmed,
    });

    return progress.hasEnoughWylds && !swapConfirmed;
  }, [
    amountMzusd,
    amountUsdc,
    mzusdAllowance,
    mzusdBalance,
    quote,
    quotedWylds,
    swapConfirmed,
    wyldsAllowance,
    wyldsBalance,
  ]);

  return {
    ...sequential,
    sections: SWAP_DEPOSIT_SECTIONS,
    isReady,
    plannedSteps,
    supportsBatching,
    quotedWylds,
    expectedWyldsDisplay: quotedWylds > 0n ? formatUnits(quotedWylds, buyDecimals) : '',
    swapSkipped,
  };
};
